import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { NAV_LINKS, business } from '@/constants/business'
import { siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [menuOpen])

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b backdrop-blur-md transition-[background-color,box-shadow,border-color] duration-300',
                scrolled || menuOpen
                    ? 'border-border bg-background/90 shadow-sm shadow-ink/5'
                    : 'border-transparent bg-foam/70'
            )}
        >
            <Container className='flex h-18 items-center justify-between gap-6'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name} home`}
                    className='rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    <Logo />
                </Link>

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-[0.95rem] font-medium text-ink/80 transition-colors hover:bg-ink/5 hover:text-ink focus-visible:outline-2 focus-visible:outline-ring'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='hidden items-center gap-4 lg:flex'>
                    <a
                        href={business.phone.href}
                        className='inline-flex items-center gap-2 rounded-full text-sm font-semibold text-ink transition-colors hover:text-aqua-deep focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                    >
                        <Phone className='size-4' />
                        {business.phone.display}
                    </a>

                    <Link href='/book' className={buttonStyles()}>
                        Book a wash
                    </Link>
                </div>

                <div className='flex items-center gap-2 lg:hidden'>
                    <Link href='/book' className={buttonStyles({ size: 'sm' })}>
                        Book
                    </Link>

                    <button
                        type='button'
                        onClick={() => setMenuOpen(open => !open)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        className='grid size-10 place-items-center rounded-full text-ink transition-colors hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-ring'
                    >
                        {menuOpen ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </Container>

            <div id='mobile-menu' hidden={!menuOpen} className='border-t border-border bg-background lg:hidden'>
                <Container className='pb-6 pt-3'>
                    <nav aria-label='Mobile'>
                        <ul className='flex flex-col'>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        onClick={() => setMenuOpen(false)}
                                        className='block rounded-xl px-3 py-3 text-base font-medium text-ink transition-colors hover:bg-foam'
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <a href={business.phone.href} className={buttonStyles({ variant: 'outline', className: 'mt-4 w-full' })}>
                        <Phone className='size-4' />
                        Call {business.phone.display}
                    </a>
                </Container>
            </div>
        </header>
    )
}
